import * as React from 'react'
import { TriggerType } from '@pushly/models/lib/enums/journeys'
import { CatalogItemType } from '@pushly/models/lib/enums'
import { extractCampaignTrigger, isTriggerTypeOf } from '../../../../_utils/campaigns'
import { generateDefaultSubscriberFcap } from '../../../../components/campaign-builder/helpers/campaign'
import {
    CAMPAIGN_DEFAULT_ABANDONMENT_METRIC,
    CAMPAIGN_DEFAULT_ABANDONMENT_SECONDS,
} from '../../../../components/campaign-builder/configuration-editor/constants'
import { AbandonmentCatalogItemTypes } from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import { CampaignModel } from '../../../../models/campaign/campaign.model'
import { CampaignConfigurationModel } from '../../../../models/campaign/campaign-configuration.model'
import { CampaignAudienceModel } from '../../../../models/campaign/campaign-audience.model'

export interface IUseTriggerHandlersArgs {
    campaign: CampaignModel
    catalogItemType: CatalogItemType
    defaultMaxAgeDays: number
    domainDefaultSegment?: { id: number } | null
    setCampaign: (c: CampaignModel) => void
    setFlags: (flags: Record<string, boolean>) => void
}

type TriggerConfigShape = {
    type?: TriggerType
    params?: Record<string, unknown>
}

export function useTriggerHandlers({
    campaign,
    catalogItemType,
    defaultMaxAgeDays,
    domainDefaultSegment,
    setCampaign,
    setFlags,
}: IUseTriggerHandlersArgs) {
    const handleTriggerTypeChange = React.useCallback(
        (type: TriggerType) => {
            const cloned = campaign.clone()
            const config: CampaignConfigurationModel = cloned.getConfiguration()
            const audience: CampaignAudienceModel = config.getAudience()
            const trigger = extractCampaignTrigger(cloned) as {
                getConfiguration: () => TriggerConfigShape
                setConfiguration: (cfg: TriggerConfigShape) => void
            }

            const catalogItemTriggerTypes: TriggerType[] = [
                TriggerType.ABANDONED_CART,
                TriggerType.ABANDONED_VIEW,
                TriggerType.SAVED_ITEM,
            ]

            const currTriggerConfig = trigger.getConfiguration()
            const currTriggerIsCatalogItem = isTriggerTypeOf(
                currTriggerConfig.type as TriggerType,
                catalogItemTriggerTypes,
            )

            // Start a typed next config
            const nextTriggerConfig: TriggerConfigShape = { ...currTriggerConfig, type }
            const nextTriggerIsCatalogItem = isTriggerTypeOf(type, catalogItemTriggerTypes)

            if (type === TriggerType.SUBSCRIBED) {
                // Reset audience to default/all
                audience.setSegmentIds(undefined)
                audience.setExcludedSegmentIds(undefined)

                // Minimal params for subscribed
                nextTriggerConfig.params = { event_type: type }
                trigger.setConfiguration(nextTriggerConfig)

                // Reset e‑commerce specific values and flags
                config.setSubscriberFrequencyCap(undefined)
                config.setItemGroupTargeting(null)
                config.setAbandonmentSeconds(null)
                config.setAbandonmentMetric(null)

                setFlags({ itemGroupsEnabled: false, quietHoursEnabled: false })
            } else if (nextTriggerIsCatalogItem) {
                // If moving from SUBSCRIBED to catalog: add initial audience
                if (!currTriggerIsCatalogItem) {
                    audience.setSegmentIds(!domainDefaultSegment ? [] : [domainDefaultSegment.id])
                }

                // Base params for catalog triggers
                nextTriggerConfig.params = { item_type: catalogItemType }

                if (type === TriggerType.ABANDONED_CART) {
                    // Add max item age for carts
                    nextTriggerConfig.params.max_item_age_days = defaultMaxAgeDays
                }

                trigger.setConfiguration(nextTriggerConfig)

                // Ensure FCAP exists for catalog triggers
                config.setSubscriberFrequencyCap(generateDefaultSubscriberFcap())

                // Ensure default values for item types that support abandonment
                if (AbandonmentCatalogItemTypes.includes(catalogItemType)) {
                    config.setAbandonmentSeconds(CAMPAIGN_DEFAULT_ABANDONMENT_SECONDS)
                    config.setAbandonmentMetric(CAMPAIGN_DEFAULT_ABANDONMENT_METRIC)
                }
            }

            // UI criteria flag reset
            setFlags({ criteriaEnabled: false })

            setCampaign(cloned)
        },
        [campaign, catalogItemType, defaultMaxAgeDays, domainDefaultSegment, setCampaign, setFlags],
    )

    return { handleTriggerTypeChange }
}
